import { ISelectOptionProps } from 'components/form/types';

type EventStyle = {
  status?: string;
  [key: string]: any;
};

// Chronological pipeline order of App\Features\WorkTrips\WorkTripEnums::STATUSES.
// `awaiting_facilitation` is a FE-facing status the backend derives from `signed_by_employee`
// for trips whose to_date hasn't passed yet - it sits before the raw `signed_by_employee`
// meaning ("trip concluded, report not yet generated") in the actual timeline.
export const STATUS_ORDER = [
  'created',
  'signed_by_employer',
  'awaiting_facilitation',
  'signed_by_employee',
  'report_created',
  'report_signed_by_employee',
  'report_signed_by_employer',
];

// Sequential single-hue-per-phase scale: blue tones darken as the trip progresses
// through signature/report stages, with the brand green reserved for the final,
// fully completed status - reads as a linear progress pipeline rather than
// arbitrary categorical colors.
const STATUS_COLORS: { [status: string]: { backgroundColor: string; color: string } } = {
  created: { backgroundColor: '#E3F2FD', color: '#37474F' },
  signed_by_employer: { backgroundColor: '#BBDEFB', color: '#37474F' },
  awaiting_facilitation: { backgroundColor: '#90CAF9', color: '#37474F' },
  signed_by_employee: { backgroundColor: '#64B5F6', color: '#fff' },
  report_created: { backgroundColor: '#42A5F5', color: '#fff' },
  report_signed_by_employee: { backgroundColor: '#1E88E5', color: '#fff' },
  report_signed_by_employer: { backgroundColor: '#716aca', color: '#fff' },
};

export const getStatusColor = (status?: string, isGeneric?: boolean): { backgroundColor: string; color: string } => {
  if (isGeneric) return { backgroundColor: '#858585', color: '#fff' };

  return (status && STATUS_COLORS[status]) || { backgroundColor: '#e0e0e0', color: '#37474F' };
};

export const getEventStyle = (e: EventStyle, isGeneric?: boolean): { [key: string]: string } | undefined => {
  if (!e) return undefined;

  const commonStyles = {
    minWidth: '110px',
    display: 'inline-block',
    padding: '0 5px',
    marginBottom: '5px',
  };

  return {
    ...commonStyles,
    ...getStatusColor(e.status, isGeneric),
  };
};

// Builds legend items from the /work-trips/dropdown-options status list (rather than
// hardcoding labels here), ordered by STATUS_ORDER, so the legend and the status filter
// dropdown stay in sync if a status is ever renamed or added on the backend.
export const buildCalendarLegendItems = (
  statusOptions?: ISelectOptionProps[]
): Array<{ value: string; label: string; backgroundColor: string; color?: string }> => {
  if (!statusOptions?.length) return [];

  return [...statusOptions]
    .sort((a, b) => STATUS_ORDER.indexOf(String(a.value)) - STATUS_ORDER.indexOf(String(b.value)))
    .map(option => ({
      value: String(option.value),
      label: option.label,
      ...getStatusColor(String(option.value)),
    }));
};
